import { useEffect, useRef } from "react";

export default function WavesBackground({
  lightColor = "#ffc524",
  darkColor = "#ffffff",
  darkestColor = "#9cbb12ff",
  opacity = 1,
}) {
  const canvasRef = useRef(null);
  const parentRef = useRef(null);
  const rafRef = useRef(0);
  const mouseRef = useRef({ x: 0, y: 0 });

  function parseColor(input) {
    const ctx = document.createElement("canvas").getContext("2d");
    ctx.fillStyle = input;
    const computed = ctx.fillStyle; // normalized css color string

    const dummy = document.createElement("div");
    dummy.style.color = computed;
    document.body.appendChild(dummy);
    const rgb = getComputedStyle(dummy).color;
    document.body.removeChild(dummy);

    const m = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (!m) return { h: 160, s: 50, l: 35 };
    const r = parseInt(m[1], 10) / 255,
      g = parseInt(m[2], 10) / 255,
      b = parseInt(m[3], 10) / 255;

    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h,
      s,
      l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        default:
          h = (r - g) / d + 4;
      }
      h *= 60;
    }
    const hsl = { h, s: s * 100, l: l * 100 };
    return hsl;
  }

  function hslToCss({ h, s, l }) {
    return `hsl(${h.toFixed(1)} ${s.toFixed(1)}% ${l.toFixed(1)}%)`;
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const parent = canvas.parentElement;
    parentRef.current = parent;

    const ctx = canvas.getContext("2d", { alpha: true });

    function fitToParent() {
      const rect = parent.getBoundingClientRect();
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    fitToParent();
    window.addEventListener("resize", fitToParent);

    // mouse parallax
    function onMouseMove(e) {
      const rect = parent.getBoundingClientRect();
      mouseRef.current.x = (e.clientX - rect.left) / rect.width - 0.5;
      mouseRef.current.y = (e.clientY - rect.top) / rect.height - 0.5;
    }

    parent.addEventListener("mousemove", onMouseMove);

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const waveLayers = [
      {
        color: hslToCss(parseColor(darkestColor)),
        amplitude: 24,
        wavelength: 540,
        speed: 1,
        y: 0.7,
        noise: 0.22,
      },
      {
        color: hslToCss(parseColor(darkColor)),
        amplitude: 24,
        wavelength: 540 / 2,
        speed: 2.5,
        y: 0.75,
        noise: 0.22,
      },
      {
        color: hslToCss(parseColor(lightColor)),
        amplitude: 24,
        wavelength: 540 / 4,
        speed: 5,
        y: 0.86,
        noise: 0.44,
      },
    ];


    function draw(time) {
      const { width, height } = canvas;
      const cssW = width / (window.devicePixelRatio || 1);
      const cssH = height / (window.devicePixelRatio || 1);

      ctx.clearRect(0, 0, cssW, cssH);
      ctx.globalAlpha = opacity;

      for (let i = 0; i < waveLayers.length; i++) {
        const layer = waveLayers[i];
        const phaseShift =
          (time / 1000) * layer.speed * (prefersReduced ? 0.25 : 1);

        // parallax offsets
        const CANON_W = 2560;
        const rawScale = cssW / CANON_W;
        const gamma = 0.5;
        const scaleX = Math.max(0.3, Math.min(1.35, Math.pow(rawScale, gamma)));

        const baseH = [1, 2, 3];
        const baseV = [0, 0.05, 0.12];

        const hOffsets = baseH.map(v => v * scaleX);
        const vOffsets = baseV;

        const px = mouseRef.current.x * hOffsets[i];
        const py = (mouseRef.current.y + 0.5) * vOffsets[i] * -1;

        drawWave({
          ctx,
          w: cssW,
          h: cssH,
          color: layer.color,
          amplitude: layer.amplitude * (prefersReduced ? 0.6 : 1),
          wavelength: layer.wavelength,
          baseY: cssH * (layer.y + py),
          phase: phaseShift + (px * (i+1)) * 0.15,
          noise: layer.noise,
        });
      }

      rafRef.current = requestAnimationFrame(draw);
    }

    function drawWave({
      ctx,
      w,
      h,
      color,
      amplitude,
      wavelength,
      baseY,
      phase,
      noise,
    }) {
      ctx.beginPath();

      ctx.moveTo(-20, h);
      ctx.lineTo(-20, baseY);

      const step = Math.max(6, Math.floor(w / 180));
      for (let x = 0; x <= w + 20; x += step) {
        const k = (x + phase * 200) / wavelength;
        const y =
          baseY +
          amplitude * Math.sin(k) * 0.9 +
          amplitude * 0.4 * Math.sin(k * 0.5 + 0.7) +
          amplitude * 0.1 * Math.sin(k * 2.4 + 1.3) * noise;
        ctx.lineTo(x, y);
      }

      ctx.lineTo(w + 20, h);
      ctx.closePath();

      const g = ctx.createLinearGradient(0, baseY - amplitude * 2, 0, h);
      g.addColorStop(0, color);
      //g.addColorStop(1, "transparent");

      ctx.fillStyle = g;
      ctx.fill();
    }

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", fitToParent);
      parent.removeEventListener("mousemove", onMouseMove);
    };
  }, [darkColor, darkestColor, lightColor, opacity]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
}
